import { resolverPortalDeLaUrl } from './tenant-basename';

/**
 * A dónde va `/<empresa>` a secas.
 *
 * 🔴 Antes no iba a ninguna parte. Con el `basename` de la empresa, la ruta que
 * queda es `/`, y ahí está la landing de marketing: entrar por
 * `prixia.datec.com.bo/datec-srl` dejaba una página que parece vacía. No se
 * detectó porque la pantalla de portales navega a `/<empresa>/dashboard`, con la
 * página puesta — nadie probó la dirección de la empresa sola, que es justo la
 * que una persona escribe o guarda en favoritos.
 */
export const RAIZ_DE_LA_EMPRESA = '/dashboard';

export interface EstadoDeLaEntrada {
  autenticado: boolean;
  /** La última empresa con la que se abrió sesión de verdad, o `null`. */
  ultimaEmpresa: string | null;
  /**
   * La ruta con la que **se abrió la página**, no la de ahora.
   *
   * 🔴 Se recibe como parámetro y NO se lee de `window.location` acá adentro, y
   * esa es la diferencia entre que esto funcione y que no. La primera versión lo
   * leía adentro, desde un `useEffect` del router: los efectos de los hijos
   * corren ANTES que los del padre, así que para cuando éste se ejecutaba algún
   * hijo ya había navegado a `/dashboard` — la comprobación «¿estoy en la raíz?»
   * daba que no, y no redirigía nunca.
   *
   * Los unitarios no lo vieron porque llamaban la función directo, sin modelar
   * que alguien ya navegó. Lo encontró el navegador, contra dev.
   */
  ruta: string;
  busqueda?: string;
  hash?: string;
}

/**
 * A dónde mandar a alguien que entró por la **raíz** del sitio, o `null` para
 * dejarlo ver la landing.
 *
 * 🔴 Por qué existe. `resolverPortalDeLaUrl` corre **una sola vez** al montar el
 * router. Entrando por `prixia.datec.com.bo`, el `basename` queda sin definir y
 * **toda la sesión queda sin empresa en la URL**: la aplicación funciona y
 * muestra datos reales, pero la dirección ya no dice de qué empresa son. Eso
 * anula la razón de ser de poner el slug en la ruta —que dos pestañas con dos
 * empresas no se pisen— y deja a la persona sin saber dónde está.
 *
 * ⚖️ La regla es angosta a propósito: **sólo la raíz exacta**. Las demás páginas
 * públicas siguen siendo públicas con sesión abierta — sacar a alguien de los
 * términos y condiciones para llevarlo a su dashboard es peor que dejarlo leer.
 */
export function destinoDeLaRaiz(estado: EstadoDeLaEntrada): string | null {
  if (!estado.autenticado) return null;
  if (estado.ruta !== '/') return null;

  const empresa = estado.ultimaEmpresa?.trim();
  if (!empresa) return null;

  /*
    🔴 Un puntero que nombra una palabra reservada no puede armar el destino:
    `/login/dashboard` no matchea ninguna ruta y caería en la landing, con un
    segmento de más y sin decir por qué. Pasa si la empresa se borró o si el
    puntero quedó corrupto. Se prefiere la landing, desde donde el ingreso
    resuelve el caso bien.
  */
  if (resolverPortalDeLaUrl(`/${empresa}`).tipo !== 'empresa') return null;

  return `/${empresa}${RAIZ_DE_LA_EMPRESA}${estado.busqueda ?? ''}${estado.hash ?? ''}`;
}
